import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useParams, useRouteMatch, withRouter } from 'react-router-dom';
import { Loading } from '../components';
import client from '../createApollo';
import { ReportSubscriptionSubscription, useSecureRespondentDetailLazyQuery } from '../generated/hooks';
import { LOGIN_NEEDED, SOMEONE_ELSE_REPORT } from '../graphql/errors';
import auth from '../lib/auth';
import { RespondentDetail } from './RespondentDetail';
import { Error } from './components/Error';
import { useReportSatismeter } from './components/hooks';

interface IReportSequenceLoaderProps extends RouteComponentProps<any> {
  accessToken: string;
  selectedLanguage: string;
  changeLanguage: (lang: string) => any;
  setReportRespondent?: (
    respondent?: NonNullable<ReportSubscriptionSubscription['getReport']['metadata']>['respondent'] | null,
  ) => void;
  setAllowedLanguages: (languages: string[]) => void;
  allowedLanguages?: string[];
}

const SecureRespondentDetail = (props: IReportSequenceLoaderProps) => {
  const { t } = useTranslation('Report');
  const { path } = useRouteMatch();
  const { respondentId, seqDate } = useParams<{ token: string; respondentId: string; seqDate: string }>();

  const [getReportSequenceRespondentDetailQuery, { data, loading, error }] = useSecureRespondentDetailLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      props.changeLanguage(data.getSecureRespondentDetail.metadata.language!.code);
    },
    onError: (error) => {
      if (error.message === LOGIN_NEEDED) {
        auth.redirectToLogin();
      }
    },
  });
  const publicAccess = path.startsWith('/process-results/');

  useEffect(() => {
    if (props.selectedLanguage !== data?.getSecureRespondentDetail.metadata.language!.code) {
      getReportSequenceRespondentDetailQuery({
        variables: {
          accessToken: props.accessToken,
          languageCode: props.selectedLanguage,
          seqDate,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedLanguage]);

  useReportSatismeter(data?.getSecureRespondentDetail.metadata, publicAccess);

  useEffect(() => {
    if (data || loading) {
      getReportSequenceRespondentDetailQuery({
        variables: {
          accessToken: props.accessToken,
          languageCode: props.selectedLanguage,
          seqDate,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [respondentId]);

  if (loading) {
    return <Loading />;
  }

  if (!loading && error) {
    return <Error>{t(error.message === SOMEONE_ELSE_REPORT ? 'someoneElseReport' : 'errorLoading')}</Error>;
  }
  if (!data || (!data.getSecureRespondentDetail.metadata.accepted && publicAccess)) {
    return <Error>{t('errorLoading')}</Error>;
  }

  return (
    <RespondentDetail
      report={data.getSecureRespondentDetail}
      selectedLanguage={props.selectedLanguage}
      changeLanguage={props.changeLanguage}
      setReportRespondent={props.setReportRespondent}
      publicAccess={publicAccess}
      isSecuredDetail={true}
      onReload={() => client.refetchQueries({ include: ['SecureRespondentDetail'] })}
    />
  );
};

export default withRouter(SecureRespondentDetail);
